import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  ComponentFactoryResolver,
  ApplicationRef,
  Injector,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { WIZ_PHYSIC_POSITION, WizDateUtils} from '@wizeoo/akm-core-lib';
import { PopUpManager, WizPopUp, WizRoot, WizEventManager } from '@wizeoo/akm-component-lib';
import { CalendarOptions, Calendar } from '@fullcalendar/core'; // l'import calendar est nécessaire, sinon ça plante
import allLocales from '@fullcalendar/core/locales-all';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { ComponentPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { DataPack, Measure, WidgetConfigurationItemObj } from '@wizeoo/akm-message-lib';
import { BaselineEngine, EM_ACTION, FeiKeyDef, FeiVO, FIELD_KIND, LoopResult, WidgetCell, WizConstants } from '@wizeoo/akm-compute-lib';
import { WidgetLine } from '@wizeoo/akm-compute-lib';

import { ALK_EVENT_TYPE, WIDGET_ACTION_ORIGIN, WIDGET_GLOBAL_KIND, WIDGET_KIND, WIDGET_STATE } from '../../../model/enums';
import { Color, colorSets } from '../../ngx-charts/utils/color-sets';
import { FastSelectData } from './../../../manager/widgets/widget.engine';
import { AlkEvent } from '../../../framework/events/alk.event';
import { WidgetBasic } from '../base/widget-basic';
import { PivotCellDeletePop } from '../pivot/renderers/cells/pivot-cell-delete.pop';
import { PivotCellEditPopData } from '../../../manager/widgets/i-widget';
import { IWidget } from '../../../manager/widgets/i-widget';
import { PopUtils } from '../../../framework/components/pop/pop-utils';
import { UpdateEntry, UpdateRequest, CompositeSourcePack } from '../../../command/core/sync/sync.service';
import { CalendarEventRenderer } from './calendar-event.renderer';
import { CalendarExtendedPop } from './pop/calendar-extended.pop';
import { CalendarAddPop, CalendarPopParams } from './calendar-add.pop';
import { ViewData } from '../../../model/view.data';
import { timelineEntry } from '../../widgets/timeline/timeline.widget';
import { styleTrio } from '../../../manager/workingdata/style.engine';
import { StyleVO } from '../../../vo/workingdata/style.vo';

@Component({
  selector: 'calendar-widget',
  templateUrl: './calendar.widget.html',
  styleUrls: ['./calendar.widget.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/** affichage des données en mode calendaire */
export class CalendarWidget extends WidgetBasic implements AfterViewInit, OnDestroy {
  
  constructor(
    elementRef: ElementRef,
    public cdref: ChangeDetectorRef,
    protected componentFactoryResolver: ComponentFactoryResolver,
    protected appRef: ApplicationRef,
    protected injector: Injector
  ) {
    super(elementRef, cdref);
    this.widgetKind = WIDGET_KIND.CALENDAR;
    this.widgetGlobalKind = WIDGET_GLOBAL_KIND.BASELINE;
    this.settingsPopClassFactory = CalendarExtendedPop;
  }
  
  /* ##############################################################################
                  composants spécifiques
  ############################################################################## */

  @ViewChild('fullcalendar') fullcalendar: FullCalendarComponent; // le calendrier

  public readyToGo = false; // le calendar est OK, sinon sale effet de refresh

  /* ##############################################################################
                  paramètres spécifiques
  ############################################################################## */

  public events = []; // la collection d'events
  public scheme: Color = colorSets[4]; // jeu de couleur

  /**
   * injection du contenu Angular sur une entrée html
   * @param info les infos du renderer de base (html)
   */
  private doMount(info) {
    const bodyPortalHost = new DomPortalOutlet(
      info.el, // Target the element where you wish to append your component
      this.componentFactoryResolver,
      this.appRef,
      this.injector
    );

    const newCompo = new ComponentPortal(CalendarEventRenderer); // renderer à injecter
    const refCompo = bodyPortalHost.attach(newCompo);
    refCompo.instance.data = info.event._def; // passage du data
  }

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin],
    editable: true,
    firstDay: 1,
    events: this.events,
    eventDidMount: this.doMount.bind(this), // injection de composant dans les renderers
    eventResizableFromStart: true,
    // eventDragStart: this.evDragStart.bind(this),
    fixedMirrorParent: document.body,
    // windowResize
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth dayGridWeek',
    },
    datesSet: this.changeDate.bind(this),
    dateClick: this.handleDateClick.bind(this),
    eventClick: this.handleEventClick.bind(this),
    eventDrop: this.alterDate.bind(this),
    eventResize: this.alterDate.bind(this),
    locales: allLocales, 
    locale: 'fr',
  };

  // private startField: string; // champ de début de plage
  // private endField: string; // champ de fin de plage
  // private colorField: string; // champ pour la couleur

    /* ##############################################################################
                    alimentation des données FEI / construction du grid
  ############################################################################### */

  /**
   * actions jouées sur le drawContent, le alterContent, ou en cas de modifs de filtre, de sélection
   * @override
   * @param origin WIDGET_ACTION_ORIGIN pour cibler qui demande
   * @param data l'objet éventuellement porté
   */
  public drawOrRefresh(origin: WIDGET_ACTION_ORIGIN, data?: any): void {
    // console.log('calendar drawOrRefresh', origin);
    let mustBuildRows: boolean = false; // on doit construire les lignes
    let mustRefresh: boolean = false; // création mise à jour de la DS de la liste

    // première création
    if(origin === WIDGET_ACTION_ORIGIN.INIT) {
      // mustBuildRows = true;
      // mustRefresh = true;
    }
    
    // première création
    if(origin === WIDGET_ACTION_ORIGIN.READY) {
      mustBuildRows = true;
      mustRefresh = true;
    }

    // fin de calcul d'un FEI requis pour mes calculs
    if(origin === WIDGET_ACTION_ORIGIN.REQUIRED_COMPUTED) {
      mustBuildRows = true;
      mustRefresh = true;
    }
    // TODO sur configuration, capter les delta filter, colonnes, fields...

    // TODO TODO détecter une modification structurelle en cas 
    // contextDamaged
    // origine: changement de config
    // et mode pivot, sinon les lignes sont toujours OK
    if(origin === WIDGET_ACTION_ORIGIN.CONFIGURE) {   
      mustBuildRows = true;
      mustRefresh = true;
    } 

    // modification du filtre global ... on filtre, et on corrige éventuellement la sélection
    if(origin === WIDGET_ACTION_ORIGIN.GLOBAL_FILTERING) {
      // mustBuildRows = true;
      mustRefresh = true;
    }


    if(origin === WIDGET_ACTION_ORIGIN.OUTER_SELECT) {
      // console.log('outer select', this.widgetKind, this.fei.uniqueName, this.state);
      mustBuildRows = true;
      mustRefresh = true;
    }

    // modification du scenario global ...
    if(origin === WIDGET_ACTION_ORIGIN.GLOBAL_SCENARIO_UPDATE) {
      mustBuildRows = true;
      mustRefresh = true;
    }

    if(origin === WIDGET_ACTION_ORIGIN.RECOMPUTE) {
      let loopResults: Map<FeiVO, LoopResult> = data[0];  
      if(this.fei.scenario === WizConstants.SCEN_KEY_CODE) {
        [...loopResults.keys()].forEach((fei: FeiVO) => {
          if(fei.visionStepLevel === this.fei.visionStepLevel) {
            if(loopResults.get(fei).created.size > 0 || loopResults.get(fei).deleted.size > 0) {
              mustBuildRows = true;
              mustRefresh = true;
            } else {
              mustRefresh = true;
            }
          }
        });
      }
      
      // gestion multiscen... prendre tous les loopers du même niveau
      let loop: LoopResult = loopResults.get(this.fei);
      if(loop?.created.size > 0 || loop?.deleted.size > 0) {
        mustBuildRows = true;
        mustRefresh = true;
      } else {
        mustRefresh = true;
      }
    }

    if(mustBuildRows) {
      this.buildLines(); // délégué au WidgetBasic
    }

    if(mustRefresh) {
      this.mainDS.refresh();
      this.buildCalendar();
    }
  }

  /**
   * construction 'initiale' des objets graphiques du widget :
   * - sur init véritable
   * - sur modification de la datasource
   * - sur modification de scenario (modifie la DS)
   */
  public drawContent(): void {
    
    let master: IWidget = this.model.widgetEngine.checkFirstMaster(this); // je cherche un master. S'il y en a un, READY n'est pas suffisant
    // si j'ai un master et qu'il n'est pas prêt, on ne fait rien
    if (!!master && master.state !== WIDGET_STATE.SELECTION_COMPLETE) { // j'ai un master, mais il n'est pas prêt
      // console.log('checkFeiState master not ready', this.widgetKind, this.fei.uniqueName);
      return;
    }

    if(this.state >= WIDGET_STATE.READY) { // déjà construit, plus rien a faire
      return;
    }

    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.INIT); // actions communes draw / alter
    super.drawContent(); // pas au début, c'est lui qui dispatch les events
    if(this.state >= WIDGET_STATE.READY) { // je viens de passer à READY, j'enchaine
      this.state = WIDGET_STATE.SELECTION_COMPLETE; // et on passe à l'état sélectionné (pour déclencher les slaves)
      WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.WIDGET_UPDATED, this));
    }
  }

  /**
   * reconstruction des objets graphiques du widget sur changement de structure : 
   * - modification des filtres qui impactent les colonnes
   * - changement de champs en ligne / colonnes / data
   * - changement de filtre
   * - changement de sélection
   * @param origin 
   */
  public alterContent(origin: WIDGET_ACTION_ORIGIN): void {
    this.drawOrRefresh(origin);
    super.alterContent(origin);
  }

    /* ##############################################################################
              cycle de vie
  ############################################################################### */

  ngAfterViewInit() {
    super.ngAfterViewInit();
    WizEventManager.addListener(this, this.elementRef, ALK_EVENT_TYPE.DELETE_CALENDAR_EVENT, this.doDelete);
    WizEventManager.addListener(this, this.elementRef, ALK_EVENT_TYPE.EDIT_CALENDAR_EVENT, this.doEdit);

  }
  ngOnDestroy() {
    super.ngOnDestroy();
    WizEventManager.removeListener(this, this.elementRef, ALK_EVENT_TYPE.DELETE_CALENDAR_EVENT);
    WizEventManager.removeListener(this, this.elementRef, ALK_EVENT_TYPE.EDIT_CALENDAR_EVENT);
  }

  /** construction graphique */
  private buildCalendar(): void {
    // console.log('buildCalendar');

    this.calendarOptions.locale = this.model.currentLocaleCode;
    this.calendarOptions.initialDate = this.getContextDate();
    this.generateEvents();

    setTimeout(() => {
      this.calendarOptions.events = this.events;
      this.readyToGo = true;
      this.cdref.markForCheck();
    }, 0);
  }

  /** construction des events calendar depuis mainDS */
  // private generateEvents(): void {
  //   this.events = [];
  //   const domLen: number = this.scheme.domain.length; // prise du pool de couleurs


  //   this.mainDS.getWorkedSource()?.forEach((wl: WidgetLine) => {
  //     let evtColor: string = null;
  //     if (!!this.colorField) {
  //       evtColor = BaselineEngine.getValue(this.fei, wl, wl.cells.get(this.colorField))
  //     }
  //     if (!evtColor) {
  //       evtColor = this.scheme.domain[this.events.length % domLen];
  //     }

  //     // alimentation de la couleur de contraste pour les rendus des libellés
  //     let contrast: string = MathUtils.invertColor(evtColor);


  //     const titleParts: string[] = [];
  //     this.context.r.forEach((wcio: WidgetConfigurationItemObj) => { // prise des éléments de titre
  //       titleParts.push(BaselineEngine.getLabel(this.fei, wl, wcio.cod, wcio.fdvf || WizConstants.FIELD_LABEL));
  //     });
      

  //     const pkdStart: FeiKeyDef = BaselineEngine.getKey(this.fei, this.startField); // la définition de colonne
  //     const pkdEnd: FeiKeyDef = BaselineEngine.getKey(this.fei, this.endField); // la définition de colonne

  //     let start: string = null;
  //     let end: string = null;
  //     if(pkdStart?.datakind === FIELD_KIND.WORKFLOW) {
  //       let settings: any = {"item":"last","fields":["startDate"]};
  //           if(!!pkdStart.measure.settings) {
  //             settings = pkdStart.measure.settings;
  //           }
  //           let strRes: string = this.model.workflowEngine.formatWorkflow(settings, BaselineEngine.getValue(this.fei, wl, wl.cells.get(this.startField)));
  //           if(strRes) {
  //             let objRes: any = JSON.parse(strRes);
  //             start = objRes.startDate;
  //           }
  //     } else {
  //       start = BaselineEngine.getValue(this.fei, wl, wl.cells.get(this.startField));
  //     }

  //     if(pkdEnd?.datakind === FIELD_KIND.WORKFLOW) {
  //         let settings: any = {"item":"last","fields":["endDate"]};
  //           if(!!pkdEnd.measure.settings) {
  //             settings = pkdEnd.measure.settings;
  //           }
  //           let strRes: string = this.model.workflowEngine.formatWorkflow(settings, BaselineEngine.getValue(this.fei, wl, wl.cells.get(this.endField)));
  //           if(strRes) {
  //             let objRes: any = JSON.parse(strRes);
  //             end = objRes.endDate;
  //           }
  //     } else {
  //       end = BaselineEngine.getValue(this.fei, wl, wl.cells.get(this.endField));
  //     }


      
 


  //     let datePart: string[] = [];
  //     datePart.push(WizDateUtils.numberToLocalizedString(Number(start)));
  //     datePart.push(WizDateUtils.numberToLocalizedString(Number(end)));


      
  //     this.events.push({
  //       editable: this.checkMe(wl),
  //       title: titleParts.join(' - '),
  //       start: WizDateUtils.dateToString(WizDateUtils.stringToTime(start), 'yyyy-MM-dd'),
  //       end: WizDateUtils.dateToString(WizDateUtils.stringToTime(end), 'yyyy-MM-dd'),
  //       color: evtColor,
  //       extendedProps: {
  //         context: this.context,
  //         widgetLine: wl,
  //         id: wl.uk,
  //         contrast: contrast,
  //         content: datePart.join(' - '),
  //         title: titleParts.join(' - '),
  //         ds: this.mainDS,
  //       },
  //     });
  //   });
  // }

    /* ##############################################################################
            Edition
  ############################################################################### */

  /** 
   * contrôle d'éligibilité au dnd 
   * 
   * @param wl la widgetline
   * @returns true si OK
   */
  private checkMe(wl: WidgetLine, startFkd: FeiKeyDef, endFkd: FeiKeyDef): boolean {

    let meas: Measure = startFkd.measure;
    if (!!meas && !!meas.editRestrict) {
      const result : boolean = this.checkEditAvailable(this.fei, meas, [wl], true);
      if (!result) {
        return false;
      }
    }

    meas = endFkd.measure;
    if (!!meas && !!meas.editRestrict) {
      const result : boolean = this.checkEditAvailable(this.fei, meas, [wl], true);
      if (!result) {
        return false;
      }
    }

    return true;
  }

  /** 
   * resize ou dnd sur un event du calendar
   * va modifier date début, fin, ou les 2
   * Déclenche le commit
   * @param arg event calendar
   */
  private alterDate(arg): void {
    const eventID: number = arg.event._def.extendedProps.id;
    const toUpdateEvent = new Map(this.events.map((e) => [e.extendedProps.id, e])).get(eventID);
    const wl: WidgetLine = toUpdateEvent.extendedProps.widgetLine; // la pl de l'event
    
    const startDate = WizDateUtils.dateToString(arg.event._instance.range.start);
    const endDate = WizDateUtils.dateToString(arg.event._instance.range.end);
    
    const startFDK: FeiKeyDef = arg.event._def.extendedProps.startFkd; 
    const endFDK: FeiKeyDef = arg.event._def.extendedProps.endFkd; 

    const updates: UpdateEntry[] = []; // les lignes à mettre à jour, encapsulées en UpdateEntry

    // mémorisation pour sync sur retour de commit
    this.toUpdateEvent = toUpdateEvent;
    this.targetStart = WizDateUtils.dateToString(arg.event._instance.range.start, 'yyyy-MM-dd');
    this.targetEnd = WizDateUtils.dateToString(arg.event._instance.range.end, 'yyyy-MM-dd');

    if(startFDK.datakind === FIELD_KIND.WORKFLOW) { // la source est un workcom
      // TODO maj seulement sur retour callback (en cas d'échec)

      this.model.workflowEngine.generateUpdateFromCalendar(this.fei, toUpdateEvent.extendedProps, startDate, endDate, this.backFromWorkcomCommit.bind(this));

    } else { // la source est un couple de dates
      updates.push(this.model.dataEngine.generateUpdate(this.fei, wl, startFDK, startDate));
      updates.push(this.model.dataEngine.generateUpdate(this.fei, wl, endFDK, endDate));

      const startMeas: Measure = startFDK.measure;
      const endMeas: Measure = endFDK.measure;
      const hasError: boolean = !this.integrityCheck(this.fei, [startMeas, endMeas], [wl], WizConstants.CHECK_MODE_CONTROL, false);

      // contrôler les 2 dates en même temps => maj baseline
      if(hasError) { // contrôle de validité de l'édition
        // WizPopUp.toast('$.common.error.$', WizIcon.TOAST_ERROR).show();
        arg.revert();
        updates.forEach((upd: UpdateEntry) => {
          this.model.dataEngine.rollBackUpdate(upd); // rollback de la valeur de la baseline / baselineValue
        });
      } else { 
        // mise à jour de l'event
        // toUpdateEvent.start = WizDateUtils.dateToString(arg.event._instance.range.start, 'yyyy-MM-dd');
        // toUpdateEvent.end = WizDateUtils.dateToString(arg.event._instance.range.end, 'yyyy-MM-dd');

        // commit
        const csp: CompositeSourcePack =  {
          updates: updates
        }
        this.model.compositeEngine.compositeCommit(csp, this.backFromCommit.bind(this));
      }
    }
  }

  // mémorisation de l'event pour sync sur retour d'update du workcom
  private toUpdateEvent: any = null;
  private targetStart: string = null;
  private targetEnd: string = null;

  private backFromWorkcomCommit(data: DataPack): void {
    // console.log('backFromWorkcomCommit', data);
    if(data.inWorkComs?.length > 0) {

      let value: string = data.inWorkComs[0].naturalUID;
      
      let toUpdateEvent = this.toUpdateEvent;
      // toUpdateEvent.start = this.targetStart;
      // toUpdateEvent.end = this.targetEnd;

      //  prise Baseline pour sync
      //  refresh event (pareil pour modif date)
      const ur: UpdateRequest = {
        fei: this.fei, 
        line: toUpdateEvent.extendedProps.widgetLine,
        lines: [toUpdateEvent.extendedProps.widgetLine], 
        operation: EM_ACTION.UPDATE,
        fkd: toUpdateEvent.extendedProps.startFkd,
        value,
        doSplit: false
      };

      let updates: UpdateEntry[] = [];
      let update: UpdateEntry;
      
      if (ur?.lines?.length > 0) {
        ur.lines.forEach((line: WidgetLine) => { // bridage selon maxCommit     
          update = this.model.dataEngine.generateUpdate(ur.fei as any, line, ur.fkd, ur.value);
          updates.push(update);
        });
      }

      // console.log('lastUpdatePile', this.lastUpdatePile);
      const csp: CompositeSourcePack =  { updates };
      this.model.compositeEngine.compositeCommit(csp, this.backFromCommit.bind(this));

    }
    
  }

  // retour de commit, synchro des events
  private backFromCommit(csp: CompositeSourcePack): void {
    // console.log('backFromCommit', csp);
    let toUpdateEvent = this.toUpdateEvent;
    toUpdateEvent.start = this.targetStart;
    toUpdateEvent.end = this.targetEnd;
    
    let goodEntry: timelineEntry = null;
    this.entries.forEach(tle => {
      if(tle.startField === toUpdateEvent.extendedProps.startFkd.fullCode) {
        goodEntry = tle;
        // console.log('backFromCommit + tle', tle);
        let evn: any = this.generateEvent(toUpdateEvent.extendedProps.widgetLine, tle);
        toUpdateEvent.title = evn.title;
        toUpdateEvent.content = evn.content;
        toUpdateEvent.extendedProps = evn.extendedProps;
      }

    });

    WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.UPDATE_CALENDAR_EVENT, toUpdateEvent));
    
    // this.cdRef.markForCheck();
    // this.buildCalendar();
  }

  /**
   * demande de suppression depuis un renderer
   * @param e AlkEvent.DELETE_CALENDAR_EVENT
   */
  private doDelete(e: AlkEvent): void {
     
    const widgetLine: WidgetLine = e.data.widgetLine;
    if (!this.fei.canDeleteItem([widgetLine])) { // gestion des droits en cas de vsp
      return;
    }

    const integrityMeas: Measure[] = [];
    this.context.i?.forEach((confItem: WidgetConfigurationItemObj) => {
      const fkd: FeiKeyDef = BaselineEngine.getKey(this.fei, confItem.cod);
      if (!!fkd && fkd.isMeasure) {
        integrityMeas.push(fkd.measure);
      }
    })

    // contrôle d'intégrité KO
    if( integrityMeas.length > 0 && !!this.context.idel && 
      !this.integrityCheck(this.fei, integrityMeas, [widgetLine])) {
      return;
    }

    let popFactory: any = PivotCellDeletePop;
    let width: string = '400px';
    let height: string = '350px';


    let datas: PivotCellEditPopData = { 
      kind: this.widgetGlobalKind, pointer: null,
      title: null, label: null, 
      value: null, column: null, fkd: null, line: widgetLine, pack: [widgetLine], widget: this, 
      
      callback: this.backFromDeletePop.bind(this)};

    const deletePop: WizPopUp = new WizPopUp(popFactory, false, false, null, width, height, datas);
    const pos: DOMRect = (e.target as Element).getBoundingClientRect();
    const bestXY: [number, number] = PopUtils.setPopCoord(pos, deletePop, WIZ_PHYSIC_POSITION.BOTTOM_RIGHT);
    const x: string = bestXY[0] + 'px';
    const y: string = bestXY[1] + 'px';
    const targetPosition: any = { left: x, top: y }; // position physique
    deletePop.customPosition = targetPosition;

    if (PopUpManager.exist(deletePop)) {
      // suppression de l'instance existante
      deletePop.hide();
    }
    deletePop.show();
  }

  /**
   * retour de popUp de demande de suppression
   * @param data PivotCellEditPopData
   */
  protected backFromDeletePop(data: PivotCellEditPopData): void {  
    
    const updateRequest: UpdateRequest = data.updates;
    // on va générer des updateEntry de type delete
    let updates: UpdateEntry[] = [];
    let update: UpdateEntry;
    if (updateRequest?.lines?.length > 0) {
      updateRequest.lines.forEach((line: WidgetLine) => { // bridage selon maxCommit
        line.bl.operation = EM_ACTION.DELETE;
        // TODO TODO vérifier
        update = this.model.dataEngine.generateUpdate(updateRequest.fei as any, line, updateRequest.fkd, updateRequest.value);
        updates.push(update);
      });
      const csp: CompositeSourcePack =  { updates };
      this.model.compositeEngine.compositeCommit(csp, this.backFromCommit.bind(this));
    }
  }

  /** sélection / déselection */
  handleEventClick(arg) {
    const wl: WidgetLine = arg.event._def.extendedProps.widgetLine;
    this.mainDS.toggle(wl);
    // mon pool est modifié, je signale aux esclaves
    this.updateComponentSelection();
    // WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.WIDGET_UPDATED, this)); 
    // fast select
    let fsd: FastSelectData = {fei: this.fei, kind: this.widgetGlobalKind, data: wl};
    WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.PIVOT_DOUBLE, fsd));
  }

  /* ##############################################################################
                    Edition en mode formulaire
     ############################################################################## */

  /** 
   * clic sur l'icone d'édition d'un event
   * 
   * @param e alkkEvent.EDIT_CALENDAR_EVENT
   * data porte la widgetLine
   */   
  private doEdit(e: AlkEvent): void {
    e.stopImmediatePropagation();
    e.preventDefault();
    this.showDatePop(e.data.widgetLine, null);
  }

  /** clic sur une date du calendrier, en dehors d'un event */
  private handleDateClick(arg) {
    const startDate: number = WizDateUtils.dateToNumber(arg.date);
    this.showDatePop(null, startDate);
  }


  /**
   * pop d'édition / ajout d'entrée dans le compo
   * @param widgetLine de l'event
   * @param startDate: la date du point d'impact
   */
  public showDatePop(widgetLine: WidgetLine, startDate: number): void {
    if (this.fei) {
      let params: CalendarPopParams = {widget: this, startDate, widgetLine, callBack: this.backFromAddPop.bind(this)};
      // ouverture de la pop
      new WizPopUp(CalendarAddPop, false, false, null, '500px', '500px', params).show();
    }
  }

  // retour de demande d'ajout, ajout véritable
  private backFromAddPop(e: any): void {}

  // génération des entrées pour la construction des events
  private entries: timelineEntry[];

  private generateEntries(): void {

    const domLen: number = this.scheme.domain.length; // prise du pool de couleurs

    // création des paires de colonnes
    let entries: timelineEntry[] = [];

    for(let i: number = 0; i < this.context.d.length; i++) {
      const start: WidgetConfigurationItemObj = this.context.d[i];
      let end: WidgetConfigurationItemObj;

      if(i< this.context.d.length - 1) { // y'a quelqu'un derrière, c'est bien une paire
        i++;
      }
      end = this.context.d[i];

      const startFkd: FeiKeyDef = BaselineEngine.getKey(this.fei, start.cod); // la définition de colonne
      const endFkd: FeiKeyDef = BaselineEngine.getKey(this.fei, end.cod); // la définition de colonne

      if(!start.ro) { // recyclage du wcio.ro : si true, pas visible
        let contentLabel: string = start.lbl ? start.lbl : this.model.feiEngine.getFeiKeyDefLabel(startFkd); 
        let backgroundColor: string = this.scheme.domain[start.ord % domLen];
        // console.log('gen', backgroundColor, i, contentLabel);
        let textColor: string = '#000000';
    

        if (!!start.csty) {
          let res: styleTrio = this.model.styleEngine.colorSet(start.csty, backgroundColor, textColor, false);
          textColor = res.color;
          backgroundColor = res.backgroundColor;

        }
  
        let entr: timelineEntry = {startField: start.cod, endField: end.cod, label: contentLabel, 
          backgroundColor, textColor, style: start.csty, measureStyle: start.msty,
          };
        
        if(startFkd?.datakind === FIELD_KIND.WORKFLOW) {
          entr.kindStart = FIELD_KIND.WORKFLOW;
          entr.settingsStart = start.pmdexParams;
        }
        if(endFkd?.datakind === FIELD_KIND.WORKFLOW) {
          entr.kindEnd = FIELD_KIND.WORKFLOW;
          entr.settingsEnd = end.pmdexParams;
        }
        entr.startFkd = startFkd;
        entr.endFkd = endFkd;
        entries.push(entr);
      }
    }
    this.entries = entries;
  }

  /**
   * génération d'UN event (pour construction initiale & update)
   * @param wl 
   * @param tle 
   */
  private generateEvent(wl: WidgetLine, tle: timelineEntry) : any {
        let start: string = null;
        if(!!tle.startField) {
          if(tle.kindStart === FIELD_KIND.WORKFLOW) {
            let settings: any = {"item":"last","fields":["startDate"]};
            if(tle.settingsStart) {
              settings = JSON.parse(tle.settingsStart);
            }
            let strRes: string = this.model.workflowEngine.formatWorkflow(settings, BaselineEngine.getValue(this.fei, wl, wl.cells.get(tle.startField)));
            if(strRes) {
              let objRes: any = JSON.parse(strRes);
              start = objRes.startDate;
            }
            
          } else {
            start = BaselineEngine.getValue(this.fei, wl, wl.cells.get(tle.startField));
          }
        }

        
        let end: string = null;
        if(!!tle.endField) {
          if(tle.kindEnd === FIELD_KIND.WORKFLOW) {
            let settings: any = {"item":"last","fields":["endDate"]};
            if(tle.settingsEnd) {
              settings = JSON.parse(tle.settingsEnd);
            }
            let strRes: string = this.model.workflowEngine.formatWorkflow(settings, BaselineEngine.getValue(this.fei, wl, wl.cells.get(tle.endField)));
            if(strRes) {
              let objRes: any = JSON.parse(strRes);
              end = objRes.endDate;
            }
          } else {
            end = BaselineEngine.getValue(this.fei, wl, wl.cells.get(tle.endField));
          }
        }


        let datePart: string[] = [];
        let cleanPart: string[] = [];
        if(start?.length > 8) { // mode time
          datePart.push(WizDateUtils.stringToCalendarTime(start));
          datePart.push(WizDateUtils.stringToCalendarTime(end));
          cleanPart.push(WizDateUtils.timeHourPart(start));
          cleanPart.push(WizDateUtils.timeHourPart(end));
        } else {
          cleanPart.push(WizDateUtils.numberToLocalizedString(Number(start)));
          cleanPart.push(WizDateUtils.numberToLocalizedString(Number(end)));  
          datePart.push(WizDateUtils.dateToString(WizDateUtils.stringToTime(start), 'yyyy-MM-dd'));
          datePart.push(WizDateUtils.dateToString(WizDateUtils.stringToTime(end), 'yyyy-MM-dd'));
        }

        // construction de la clé d'appartenance à la ressource
        const keyParts: string[] = [];
        this.context.r.forEach((wcio: WidgetConfigurationItemObj) => {    
          let part: string = BaselineEngine.getLabel(this.fei, wl, wcio.cod, wcio.fdvf || WizConstants.FIELD_LABEL);
          keyParts.push(part);
        });
        let key: string = keyParts.join(WizConstants.KEY_SEPARATOR);
        // let title: string = titleParts.join(' - ');

        // prise des couleurs, on va éventuellement jouer un style
        let realtextColor: string = tle.textColor;
        let realbackgroundColor: string = tle.backgroundColor;
        
        // console.log('beforeCss', realtextColor, realbackgroundColor);
        if(tle.measureStyle) {
          try {
            const styleParts: string[] = tle.measureStyle.split(ViewData.KEY_SEPARATOR);
            let fontM;
            let backM;
            let cell: WidgetCell;
            let r;
            if (!!styleParts[0]) {
              fontM = styleParts[0];

              cell = wl.cells?.get(fontM);
              if(cell) {
                r = this.fei.model.cellMatrixEngine.getCellValue(cell.l, cell.k, cell.sc, cell.m);
                if(!!r){
                  realtextColor = r;
                  // console.log('measureStyle front', tle.measureStyle, fontM, backM, wl.cells, r);
                }
              }
            }
            if (!!styleParts[1]) {
              backM = styleParts[1];
              cell = wl.cells?.get(backM);
              if(cell) {
                r = this.fei.model.cellMatrixEngine.getCellValue(cell.l, cell.k, cell.sc, cell.m);
                if(!!r){
                  realbackgroundColor = r;
                  // console.log('measureStyle back', tle.measureStyle, fontM, backM, wl.cells, r);
                }
              }
            }
            // console.log('measureStyle', tle.measureStyle, fontM, backM, wl.cells);
            
          } catch(e: any) {

          }

        }
             
        
        if(tle.style) {
          let parts: string[] = tle.style.split(ViewData.KEY_SEPARATOR); // nommage des style vision_code#code 
          const style: StyleVO = this.model.styleEngine.getStyle(parts[0], parts[1]);
          if (!!style) {
            let neoCss = this.model.widgetEngine.computeStyleForWidgetLine(this.fei, style, wl);
            if(!!neoCss?.color && neoCss?.color !== 'undefined') {
              realtextColor = neoCss.color;
              // console.log('style front', tle.style, realtextColor);
            }
            if(neoCss?.backgroundColor && neoCss?.backgroundColor !== 'undefined') {
              realbackgroundColor = neoCss.backgroundColor;
            }
            // console.log('neoCss', neoCss, realtextColor, realbackgroundColor);
          }
        }

        // label de base du champs
        const titleParts: string[] = [];
        titleParts.push(tle.label);
  
        
        if(this.context.c.length > 0) {  
          this.context.c.forEach((wcio: WidgetConfigurationItemObj) => {
            let fkd: FeiKeyDef = this.fei.keyMap.get(wcio.cod);
            let part: string;
            if(fkd.isMeasure) {
              part = BaselineEngine.getValue(this.fei, wl, wl.cells.get(wcio.cod));
            } else {
              // filter ou Key
              part = BaselineEngine.getLabel(this.fei, wl, wcio.cod, wcio.fdvf || WizConstants.FIELD_LABEL);
            }
            if(fkd.datakind === FIELD_KIND.OBJECT) {
              part = this.model.freedataEngine.getSubFDV(part);
            } else if(fkd.datakind === FIELD_KIND.WORKFLOW) {
              if(part) {
                try {
                  part = this.model.workflowEngine.formatWorkflow(tle.settingsStart, part);
                  let smallMess: any = JSON.parse(part);  
                  if(smallMess.keys) {      
                    part = smallMess.keys.join(" - ");
                  }
                } catch(e: any) {
                  console.log(e, part);
                }

              }
              
            }
            if(!!part) {
              titleParts.push(part);
            } 
          });
        }      
        let realLabel: string = titleParts.join(' - ');
      
        

        // let contrast: string = MathUtils.invertColor(evtColor);
        
        let eventId: string = [wl.uk, tle.startField].join(ViewData.CONTEXT_SEPARATOR);
        let evn = {
            id: eventId, 
            resourceId: key, // wl.uk,
            editable: this.checkMe(wl, tle.startFkd, tle.endFkd),
            title: realLabel,
            start: datePart[0],
            end: datePart[1],
            textColor: realtextColor,
            backgroundColor: realbackgroundColor,
            borderColor: '#00000000',
            extendedProps: {
              context: this.context,
              title: realLabel,
              content: cleanPart.join(' - '),
              color: realtextColor,
              startFkd: tle.startFkd,
              endFkd: tle.endFkd,
              freedataValue: null, 
              widgetLine: wl,
              id: eventId,
              ds: this.mainDS,
              //contrast: contrast
            }
          };
    return evn;
  }


  /** construction des events calendar depuis mainDS */
  private generateEvents(): void {
    this.events = [];

    this.generateEntries();

    if(this.entries?.length <= 0) { // vide, pas de datas
      return;
    }

    this.mainDS.getWorkedSource().forEach((wl: WidgetLine) => { 
      this.entries.forEach((tle: timelineEntry) => {
          let evn = this.generateEvent(wl, tle);

          // console.log('evn', evn.textColor, evn.extendedProps.color, evn.backgroundColor)
          this.events.push(evn);
      });
    });
    // console.log('generateEvents', this.events);
  }





}
